import { CommodityMilestone, MilestoneStatusCode } from '@bechtel/types';
import { isWithinInterval, parseISO } from 'date-fns';
import { concat, filter, findIndex, includes, some, map } from 'lodash';
import { usePlanning, useAppUser, useWorkPackageOwner } from '@bechtel/client-common';
import { usePlanningPermissions } from '../../../hooks';

//commodityMilestones already grouped based on commodityid from calling component
export function useDragPermission(commodityMilestones: CommodityMilestone[]): boolean {
  const { canPlanAssignedMilestones, canPlanAreaMilestones } = usePlanningPermissions();
  const { user } = useAppUser();
  const userFacilities = user?.facilities ? user?.facilities : [];
  const { weekStartDate, weekEndDate, selectedForeman } = usePlanning();
  const { workPackage } = commodityMilestones[0].commodity.weld || commodityMilestones[0].commodity.materialCommodity;
  const { startDate: wpStartDate, endDate: wpEndDate, primaryOwners, secondaryOwners, otherOwners } = workPackage;
  const workOwners = concat(primaryOwners, secondaryOwners, otherOwners);
  const isOwner = useWorkPackageOwner(workOwners);
  const workPackageFacilities = workPackage.facilities ? workPackage.facilities : [];
  const isFacilityOwner =
    !workPackageFacilities || workPackageFacilities.length == 0 || !userFacilities || userFacilities.length == 0
      ? false
      : some(workPackageFacilities, (wpf) =>
          includes(
            map(userFacilities, (userFacility) => userFacility.id),
            wpf.id
          )
        );

  if (
    !isOwner ||
    findIndex(
      workOwners,
      (owner) =>
        owner &&
        owner.persona &&
        owner.crewMember &&
        owner.crewMember.id &&
        owner.persona == selectedForeman?.persona &&
        owner.crewMember.id == selectedForeman?.id
    ) == -1
  )
    return false;

  const parsedWpStartDate = wpStartDate && parseISO(wpStartDate);
  const parsedWpEndDate = wpEndDate && parseISO(wpEndDate);
  const parsedWeekStartDate = weekStartDate && parseISO(weekStartDate);
  const parsedWeekEndDate = weekEndDate && parseISO(weekEndDate);
  const isPlannable =
    parsedWpStartDate &&
    parsedWpEndDate &&
    parsedWeekStartDate &&
    parsedWeekEndDate &&
    parsedWpStartDate < parsedWpEndDate &&
    (isWithinInterval(parsedWpStartDate, { start: parsedWeekStartDate, end: parsedWeekEndDate }) ||
      isWithinInterval(parsedWpEndDate, { start: parsedWeekStartDate, end: parsedWeekEndDate }) ||
      isWithinInterval(parsedWeekStartDate, { start: parsedWpStartDate, end: parsedWpEndDate }) ||
      isWithinInterval(parsedWeekEndDate, { start: parsedWpStartDate, end: parsedWpEndDate }));
  if (!isPlannable) return false;

  const plannableMilestones = filter(commodityMilestones, (item: CommodityMilestone) => {
    return (
      item.currentStatus.codeI18n === MilestoneStatusCode.UNPLANNED ||
      item.currentStatus.codeI18n === MilestoneStatusCode.PLANNED ||
      item.currentStatus.codeI18n === MilestoneStatusCode.REJECTED ||
      item.currentStatus.codeI18n === MilestoneStatusCode.BLOCKED
    );
  });
  return (
    ((canPlanAssignedMilestones && isOwner) || (canPlanAreaMilestones && isFacilityOwner)) &&
    isPlannable &&
    plannableMilestones.length === commodityMilestones.length
  );
}
